/**
 * AppConfig.js - Centralized application configuration
 *
 * This file contains all configuration values that might need to be changed
 * across environments or during development. It prevents hardcoding values
 * throughout the application.
 */

import spotlightService from '../services/SpotlightService';
import { clientEnv } from '../buildTimeEnv';
import { getDspApiKey } from '../runtimePublicEnv';

/**
 * DotPlayer Configuration
 */
export const DOTPLAYER_CONFIG = {
  // Default player settings
  DEFAULT_SETTINGS: {
    autostart: true,
    controls: true,
    muted: false,
    fluid: true,
    skin: true,
    theme: {
      fontColor: '#ffffff',
      fontColorHover: '#56bdaf',
      progressSliderMain: '#56bdaf',
      progressSliderBackground: '#aaaaaa',
      progressSliderBuffered: '#cccccc',
    },
  },

  // Player timeouts and delays
  MOUNT_DELAY: 200, // ms to wait before mounting player
  PRELOADER_TIMEOUT: 300, // ms to show preloader
  PLAYER_LOAD_TIMEOUT: 10000, // ms to wait for player to load
};

/**
 * API Configuration
 */
export const API_CONFIG = {
  // Base URLs
  BASE_URL: clientEnv.REACT_APP_API_URL || 'https://api.myspotlight.tv',
  CDN_URL: clientEnv.REACT_APP_CDN_URL || 'https://dprodspmedia.cachefly.net',

  // Request timeouts
  REQUEST_TIMEOUT: 10000, // ms
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // ms
};

/**
 * Application Settings
 */
export const APP_CONFIG = {
  // Application metadata
  APP_NAME: 'ChillFree',
  APP_VERSION: clientEnv.REACT_APP_VERSION || '1.0.0',

  // Feature flags
  FEATURES: {
    LIVE_TV: true,
    SEARCH: true,
    WATCHLIST: true,
    SERIES_SUPPORT: true,
    HOVER_PREVIEWS: true,
  },

  // UI Settings
  UI: {
    HOVER_DELAY: 500, // ms before showing hover modal
    HIDE_DELAY: 300, // ms before hiding hover modal
    OVERLAY_TIMEOUT: 3000, // ms before hiding watch page overlay
    ANIMATION_DURATION: 300, // ms for transitions
  },

  // Cache settings
  CACHE: {
    DEFAULT_TTL: 5 * 60 * 1000, // 5 minutes
    IMAGE_TTL: 30 * 60 * 1000, // 30 minutes
    VIDEO_TTL: 2 * 60 * 1000, // 2 minutes
  },
};

/**
 * Environment Detection
 */
export const ENVIRONMENT = {
  IS_DEVELOPMENT: process.env.NODE_ENV === 'development',
  IS_PRODUCTION: process.env.NODE_ENV === 'production',
  IS_TEST: process.env.NODE_ENV === 'test',
};

/**
 * Cache for app load data to avoid repeated API calls
 */
let appLoadCache = null;
let appLoadPromise = null;
let companyIdCache = null;
let cacheTimestamp = null;

// Cache TTL: 1 hour (company ID shouldn't change frequently)
const COMPANY_ID_CACHE_TTL = 60 * 60 * 1000;

/**
 * Check if cached company ID is still valid
 * @returns {boolean} True if cache is valid
 */
const isCacheValid = () => {
  if (!companyIdCache || !cacheTimestamp) return false;
  return Date.now() - cacheTimestamp < COMPANY_ID_CACHE_TTL;
};

/**
 * Get company ID from app load call with robust caching
 * @param {boolean} forceRefresh - Force refresh of cached value
 * @returns {Promise<string>} Company ID from app data
 */
export const getCompanyId = async (forceRefresh = false) => {
  try {
    // Return cached value if valid and not forcing refresh
    if (!forceRefresh && isCacheValid() && companyIdCache) {
      return companyIdCache;
    }

    // If already fetching, wait for existing promise
    if (appLoadPromise) {
      const appData = await appLoadPromise;
      const companyId = appData?.app_data?.company_id;
      if (companyId) {
        companyIdCache = companyId;
        cacheTimestamp = Date.now();
        return companyId;
      }
    }

    // Fetch app load data
    appLoadPromise = spotlightService.getAppLoad();
    const appData = await appLoadPromise;

    // Cache the result
    appLoadCache = appData;
    appLoadPromise = null;

    const companyId = appData?.app_data?.company_id;

    if (!companyId) {
      companyIdCache = '6419d68d33c59c85e30cdc62'; // Fallback for safety
      cacheTimestamp = Date.now();
      return companyIdCache;
    }

    // Cache the company ID with timestamp
    companyIdCache = companyId;
    cacheTimestamp = Date.now();

    return companyId;
  } catch (error) {
    appLoadPromise = null; // Clear failed promise

    // Return cached value if available, even if expired
    if (companyIdCache) {
      return companyIdCache;
    }

    // Return fallback company ID if no cache available
    const fallbackId = '6419d68d33c59c85e30cdc62';
    companyIdCache = fallbackId;
    cacheTimestamp = Date.now();
    return fallbackId;
  }
};

/**
 * Tenant-identity params forwarded to MediaTailor via the SDK's /play call.
 * app_bundle uses the bare hostname (no www.) to match ads.txt — SSPs flag
 * reverse-DNS bundles on web as fraud.
 */
export const getAdTagParams = () => ({
  app_bundle: window.location.hostname.replace(/^www\./, ''),
  app_name: APP_CONFIG.APP_NAME,
  app_version: APP_CONFIG.APP_VERSION,
});

/**
 * Helper function to get DotPlayer mount configuration
 * @param {string} videoId - Video ID to play
 * @param {string} target - DOM target selector
 * @param {Object} overrides - Settings to override defaults
 * @param {Object} options - Additional options for ad control
 * @param {boolean} options.adsEnabled - Whether ads should be loaded (from subscription_access.ads_enabled)
 * @returns {Promise<Object>} Complete DotPlayer mount configuration
 */
export const getDotPlayerConfig = async (videoId, target = '#pdp-player', overrides = {}, options = {}) => {
  const companyId = await getCompanyId();

  // Get client token for authenticated playback (required for ad-free streams after purchase)
  const clientToken = spotlightService.getClientTokenSync();

  const config = {
    video_id: videoId,
    company_id: companyId,
    target,
    adTagParams: getAdTagParams(),
    ...DOTPLAYER_CONFIG.DEFAULT_SETTINGS,
    ...overrides,
  };

  // Add token if available - this tells the backend the user's subscription status
  // The DotPlayer SDK expects 'token' (not 'client_token') for user authentication
  if (clientToken) {
    config.token = clientToken;
    console.log('🎬 DotPlayer: Including token for authenticated playback');
  } else {
    console.log('🎬 DotPlayer: No token available (unauthenticated playback)');
  }

  // Handle ad-free playback: If ads_enabled is explicitly false, disable ads completely
  // This is controlled by subscription_access.ads_enabled from the channel/video data
  const { adsEnabled } = options;
  if (adsEnabled === false) {
    // Multiple flags to ensure ads are completely disabled:
    // 1. ad_blocker_detected: true - tells player to skip IMA initialization  
    // 2. interruptions: false - disables ad break scheduling (required for IMA init)
    // 3. show_interruptions: false - hides ad UI elements
    // 4. adTagUrl: '' - empty string to prevent VMAP loading
    config.ad_blocker_detected = true;
    config.interruptions = false;
    config.show_interruptions = false;
    config.adTagUrl = '';
    // Also set on nested video object in case SDK looks there
    config.video = config.video || {};
    config.video.interruptions = false;
    config.video.vmap_url = null;
    config.video.vmap = null;
    console.log('🎬 DotPlayer: Ads DISABLED - Config:', {
      ad_blocker_detected: config.ad_blocker_detected,
      interruptions: config.interruptions,
      adTagUrl: config.adTagUrl,
      video_interruptions: config.video?.interruptions
    });
  } else {
    console.log('🎬 DotPlayer: Ads enabled (IMA plugin will load ads)');
  }

  return config;
};

/**
 * Helper function to validate required environment variables
 * @returns {Object} Validation results
 */
export const validateConfig = () => {
  const errors = [];
  const warnings = [];

  // Check required environment variables
  if (!API_CONFIG.BASE_URL) {
    errors.push('REACT_APP_API_URL is required');
  }

  if (!getDspApiKey()) {
    errors.push('REACT_APP_DSP_API_KEY is required for app load');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Clear app load cache (useful for testing or when switching environments)
 */
export const clearAppLoadCache = () => {
  appLoadCache = null;
  appLoadPromise = null;
  companyIdCache = null;
  cacheTimestamp = null;
};

/**
 * Get cache status for debugging
 * @returns {Object} Cache status information
 */
export const getCacheStatus = () => {
  return {
    hasCompanyIdCache: !!companyIdCache,
    companyIdValue: companyIdCache,
    cacheAge: cacheTimestamp ? Date.now() - cacheTimestamp : null,
    isValid: isCacheValid(),
    ttl: COMPANY_ID_CACHE_TTL,
  };
};

// Validate configuration on import in development
if (ENVIRONMENT.IS_DEVELOPMENT) {
  const validation = validateConfig();
  if (!validation.isValid) {
    console.error('❌ Configuration errors:', validation.errors);
  }
  if (validation.warnings.length > 0) {
    console.warn('⚠️ Configuration warnings:', validation.warnings);
  }
}

export default {
  DOTPLAYER_CONFIG,
  API_CONFIG,
  APP_CONFIG,
  ENVIRONMENT,
  getCompanyId,
  getDotPlayerConfig,
  getAdTagParams,
  validateConfig,
  clearAppLoadCache,
  getCacheStatus,
};
